import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  Typography,
} from '@mui/material';

import { naturalSortByProperty } from '@paragon/utilities';

import {
  deleteBatch,
  removeProductionComponentInstancesFromBatchByAnalysisSetGuid,
} from '../../../actions/batchActions';
import {
  getBatchesForActiveProductionGroupGuid,
  getQuantitiesForActiveProductionGroupByBatchGuidAndAnalysisSetGuid,
  getRoundedLinearFeetSumsByBatchGuid,
} from '../../../selectors/batches';
import { getLoadedTrussesByAnalysisSetGuid } from '../../../selectors/trusses';
import { collapseBatch, expandBatch, setBatchBeingEditedGuid } from '../../../store/batchesAndBundlesTabSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';

export function BatchesAccordion() {
  const dispatch = useAppDispatch();
  const batchesForActiveProductionGroup = useAppSelector(getBatchesForActiveProductionGroupGuid);
  const quantitiesForActiveProductionGroupByBatchGuidAndAnalysisSetGuid = useAppSelector(
    getQuantitiesForActiveProductionGroupByBatchGuidAndAnalysisSetGuid,
  );
  const expandedBatches = useAppSelector((state) => state.batchesAndBundlesTab.batchesExpanded);
  const batchBeingEditedGuid = useAppSelector((state) => state.batchesAndBundlesTab.batchBeingEditedGuid);
  const loadedTrussesByAnalysisSetGuid = useAppSelector(getLoadedTrussesByAnalysisSetGuid);
  const roundedLinearFeetSumsByBatchGuid = useAppSelector(getRoundedLinearFeetSumsByBatchGuid);

  if (batchesForActiveProductionGroup === null)
    return <Typography>Select a production group to view its batches.</Typography>;

  return (
    <Box sx={{ height: '1px', flexGrow: 1, position: 'relative', overflow: 'auto' }}>
      {batchesForActiveProductionGroup.map((batch) => {
        const quantitiesForBatchByAnalysisSetGuid =
          quantitiesForActiveProductionGroupByBatchGuidAndAnalysisSetGuid[batch.guid];
        const quantitiesByAnalysisSetGuidSorted = naturalSortByProperty(
          Object.entries(quantitiesForBatchByAnalysisSetGuid),
          ([analysisSetGuid]) => loadedTrussesByAnalysisSetGuid[analysisSetGuid]?.name,
        );
        return (
          <Accordion
            expanded={expandedBatches.includes(batch.guid)}
            onChange={(event, expanded) =>
              expanded ? dispatch(expandBatch(batch.guid)) : dispatch(collapseBatch(batch.guid))
            }
            key={batch.guid}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Grid container spacing={1} sx={{ width: '100%' }}>
                <Grid size={8} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography>{`Batch ${batch.id} (${roundedLinearFeetSumsByBatchGuid[batch.guid]} LF)`}</Typography>
                </Grid>
                <Grid size={2} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={(event) => {
                      event.stopPropagation();
                      dispatch(expandBatch(batch.guid));
                      batch.guid === batchBeingEditedGuid
                        ? dispatch(setBatchBeingEditedGuid(null))
                        : dispatch(setBatchBeingEditedGuid(batch.guid));
                    }}
                  >
                    {batch.guid === batchBeingEditedGuid ? 'Done' : 'Edit'}
                  </Button>
                </Grid>
                <Grid size={2} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <IconButton
                    onClick={() => dispatch(deleteBatch(batch))}
                    disabled={batch.guid !== batchBeingEditedGuid}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                {quantitiesByAnalysisSetGuidSorted.map(([analysisSetGuid, quantity]) => (
                  <ListItem
                    key={analysisSetGuid}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() =>
                          dispatch(
                            removeProductionComponentInstancesFromBatchByAnalysisSetGuid(analysisSetGuid, batch.guid),
                          )
                        }
                        disabled={batch.guid !== batchBeingEditedGuid}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <Typography>
                      {`${loadedTrussesByAnalysisSetGuid[analysisSetGuid]?.name ?? 'Loading'} (${quantity})`}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
